import { useState } from 'react';
import { toast } from 'sonner';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { shareMessage, shareTargets, shortUrl } from '@/shared/referralLink';
import { cn } from '@/lib/utils';
import { useT } from '@/shared/i18n';
import { A } from '@/shared/locales/account/keys';

/** A buyer's referral link with copy, native share and one-tap share buttons. */
export default function ReferralLinkBox({ username, url, live = true, compact = false, className }) {
  const { t } = useT();
  const [copied, setCopied] = useState(false);
  const canShare = typeof navigator !== 'undefined' && !!navigator.share;

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(t(A.linkCopied));
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(t(A.cantCopyLink));
    }
  }

  async function share() {
    try {
      await navigator.share({ title: 'Ghost Cable', text: shareMessage(username, url), url });
    } catch (e) {
      if (e?.name !== 'AbortError') copy();
    }
  }

  return (
    <div className={cn('space-y-3', className)}>
      <div className="rounded-md border border-dashed border-highlight/50 bg-highlight/5 px-3 py-2.5">
        <span className="block text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{t(A.yourLink)}</span>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="block break-all font-mono text-sm text-highlight hover:underline"
        >
          {shortUrl(url)}
        </a>
        <span className="mt-1 block text-xs text-muted-foreground">
          {t(A.usernameOn)} <span className="font-mono text-foreground">{username}</span>
          {compact ? '' : ` ${t(A.fromFirst)}`}
        </span>
      </div>

      {!live && (
        <p className="flex items-start gap-2 rounded-md border border-warning-border bg-warning-muted px-3 py-2 text-xs text-warning">
          <ApperIcon name="CircleAlert" size={14} className="mt-0.5 shrink-0" />
          {t(A.notActive)}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" onClick={copy}>
          <ApperIcon name={copied ? 'Check' : 'Copy'} size={16} />
          {copied ? t(A.copied) : t(A.copyLink)}
        </Button>
        {canShare && (
          <Button type="button" size="sm" variant="outline" onClick={share}>
            <ApperIcon name="Share2" size={16} />
            {t(A.share)}
          </Button>
        )}
      </div>

      {!compact && (
        <div className="flex flex-wrap gap-2" aria-label={t(A.share)}>
          {shareTargets(username, url).map((x) => (
            <Button key={x.id} type="button" size="sm" variant="ghost" asChild>
              <a href={x.href} target="_blank" rel="noreferrer">
                <ApperIcon name={x.icon} size={16} />
                {x.label}
              </a>
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
